import React, {
  FunctionComponent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import {
  UserToolbar,
  ToolbarContextSections,
  ContextData,
} from '@wix/answers-common-components';
import { Counters, CountersResult, CountersHelper } from '@wix/answers-api';
import { useTranslation } from '@wix/wix-i18n-config';
import { AGENT_TOOLBAR, BI, PAGES } from '../../constants';
import { useRouter } from 'next/router';
import { isLoggedInAsAgentInApp } from './agent-checker';
import { PageData } from '../PageTemplate';
import { Context } from '../../context';
import { PageType } from '../../types';
import { clickTheFollowUnFollowButton } from '@wix/bi-logger-new-help-center/v2';
import { clickTheFollowUnFollowButtonParams } from '@wix/bi-logger-new-help-center/v2/types';
import { useBI } from '../../hooks/useBI';
import { mapArticleTypeToItemType } from '../../utils/bi';
import {
  hasAnswersSSO,
  isUserLoggedIn,
  loginWithAnswersSSO,
} from '../../utils/login';

type AgentToolbarProps = {
  pageType: PageType;
  pageData: PageData;
};

export const AgentToolbar: FunctionComponent<AgentToolbarProps> = ({
  pageType,
  pageData,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const { answersApi } = useContext(Context);
  const { t } = useTranslation();
  const { sendBIEvent } = useBI();
  const [contextData, setContextData] = React.useState<ContextData>({});
  const [counters, setCounters] = React.useState<Counters>();
  const [isAgent, setIsAgent] = React.useState(false);
  const [isFollowing, setIsFollowing] = useState<boolean>(false);
  const [isFollowingCategory, setIsFollowingCategory] =
    useState<boolean>(false);

  const { locale } = useRouter();

  const initIsAgent = useCallback(() => {
    isLoggedInAsAgentInApp()
      .then((val) => {
        let isAgentConnected = false;
        if (isUserLoggedIn()) {
          isAgentConnected = val;
        }
        setTimeout(() => {
          setIsAgent(isAgentConnected);
          if (isAgentConnected && !hasAnswersSSO()) {
            loginWithAnswersSSO(location.href, locale as string);
          }
        }, 850);
      })
      .catch(() => setIsAgent(false));
  }, [locale]);

  useEffect(() => {
    initIsAgent();
  }, [initIsAgent]);

  const fetchCounters = useCallback(async () => {
    const { article } = pageData;
    if (pageType === PAGES.ARTICLE && article?.id && isAgent) {
      const res = await answersApi.articles.getArticleCounters({
        id: article?.id,
        locale: locale as string,
      });
      setCounters(
        CountersHelper.convertCounterResultsToCounters(
          res as unknown as CountersResult[]
        )
      );
    }
  }, [locale, isAgent, pageData, pageType, answersApi]);

  useEffect(() => {
    void fetchCounters();
  }, [fetchCounters]);

  const fetchIsFollowing = useCallback(async () => {
    let isFollowingResponse = false;
    const { article, category } = pageData;
    switch (pageType) {
      case PAGES.ARTICLE:
        if (article?.id) {
          isFollowingResponse = await answersApi.articles.isFollowingArticle({
            id: article?.id,
            locale: locale as string,
          });
        }
        setIsFollowing(isFollowingResponse);
        break;
      case PAGES.CATEGORY:
        if (category?.id) {
          isFollowingResponse = await answersApi.kb.isFollowingCategory({
            id: category?.id,
            locale: locale as string,
          });
          setIsFollowingCategory(isFollowingResponse);
        }
        break;
      default:
        break;
    }
  }, [locale, pageType, pageData, answersApi]);

  useEffect(() => {
    if (isAgent) {
      void fetchIsFollowing();
    }
  }, [fetchIsFollowing, isAgent]);

  const ToolbarContextSectionsMap = {
    [PAGES.ARTICLE]: ToolbarContextSections.ARTICLE,
    [PAGES.CATEGORY]: ToolbarContextSections.CATEGORY,
    [PAGES.SEARCH_RESULTS]: ToolbarContextSections.NONE,
    [PAGES.HOME]: ToolbarContextSections.NONE,
    [PAGES.ROADMAP]: ToolbarContextSections.NONE,
  };

  const articleFollowToggle = useCallback(
    async (isFollow: boolean) => {
      const { article } = pageData;
      if (article?.id) {
        if (isFollow) {
          await answersApi.articles.followArticle({
            id: article.id,
            locale: locale as string,
          });
        } else {
          await answersApi.articles.unfollowArticle({
            id: article.id,
            locale: locale as string,
          });
        }
      }
    },
    [locale, pageData, answersApi]
  );

  const categoryFollowToggle = useCallback(
    async (isFollow: boolean) => {
      const { category } = pageData;
      if (category?.id) {
        if (isFollow) {
          await answersApi.kb.followCategory({
            id: category.id,
            locale: locale as string,
          });
        } else {
          await answersApi.kb.unfollowCategory({
            id: category.id,
            locale: locale as string,
          });
        }
      }
    },
    [locale, pageData, answersApi]
  );

  const followToggle = useCallback(
    async (isFollow: boolean) => {
      const eventFields: clickTheFollowUnFollowButtonParams = {
        lang: locale,
        thumbs_up: isFollow,
      };
      const { category, article } = pageData;
      switch (pageType) {
        case PAGES.ARTICLE:
          await articleFollowToggle(isFollow);
          setIsFollowing(isFollow);
          eventFields.source_name = BI.SOURCE_NAMES.ARTICLE;
          eventFields.item_id = article?.id;
          eventFields.item_type = mapArticleTypeToItemType(article?.type);
          break;
        case PAGES.CATEGORY:
          await categoryFollowToggle(isFollow);
          setIsFollowingCategory(isFollow);
          eventFields.source_name = BI.SOURCE_NAMES.CATEGORY;
          eventFields.item_id = category?.id;
          eventFields.item_type = BI.SOURCE_NAMES.CATEGORY;
      }
      await sendBIEvent(clickTheFollowUnFollowButton(eventFields));
    },
    [
      pageType,
      articleFollowToggle,
      categoryFollowToggle,
      sendBIEvent,
      pageData,
      locale,
    ]
  );

  useEffect(() => {
    const { article, category } = pageData;
    switch (pageType) {
      case PAGES.ARTICLE:
        if (article) {
          setContextData({
            articleContext: {
              article,
              updated: getFormattedDate(article.lastUpdateDate),
              created: getFormattedDate(article.creationDate),
              isFollowing: !!isFollowing,
              onFollowToggle: followToggle,
              counters,
            },
          });
        }
        break;
      case PAGES.CATEGORY:
        if (category) {
          setContextData({
            categoryContext: {
              isFollowing: !!isFollowingCategory,
              onFollowToggle: followToggle,
            },
          });
        }
        break;
    }
  }, [pageData, pageType, counters, isFollowing, isFollowingCategory]);

  useEffect(() => {
    const bodyElm = document.body;
    if (isExpanded && isAgent) {
      bodyElm.classList.add(AGENT_TOOLBAR.CLASSES.OPEN);
      bodyElm.classList.remove(AGENT_TOOLBAR.CLASSES.CLOSE);
    } else {
      bodyElm.classList.add(AGENT_TOOLBAR.CLASSES.CLOSE);
      bodyElm.classList.remove(AGENT_TOOLBAR.CLASSES.OPEN);
    }
  }, [isExpanded, isAgent]);

  return (
    <>
      <iframe
        style={{ display: 'none' }}
        title="AnswersAgentChecker"
        id="agent-check-iframe"
        src="https://wix.wixanswers.com/agent-check"
      />
      {isAgent && (
        <UserToolbar
          isExpanded={isExpanded}
          toggleExpansion={() => setIsExpanded(!isExpanded)}
          translate={t}
          visibleContext={
            ToolbarContextSectionsMap[pageType] || ToolbarContextSections.NONE
          }
          contextData={contextData}
        />
      )}
    </>
  );
};

const getFormattedDate = (date: number) =>
  new Date(date).toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
